import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { LogIn, ShieldCheck } from 'lucide-react';
import { api, type AuthConfig, type AuthProviderStatus, type AuthUser } from '../lib/api';

interface Props {
  children: (user: AuthUser, onLogout: () => void) => ReactNode;
}

const DEFAULT_PROVIDER_STATUS: AuthProviderStatus = {
  configured: false,
  enabled: false,
  available: false,
};

export default function AuthGate({ children }: Props) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  const callbackError = useMemo(() => {
    const raw = new URLSearchParams(window.location.search).get('auth_error');
    return raw ? decodeURIComponent(raw) : null;
  }, []);

  useEffect(() => {
    const { pathname, search, hash } = window.location;
    const normalizedPath = pathname.replace(/\/{2,}/g, '/');
    if (normalizedPath === '/auth/callback' && pathname !== normalizedPath) {
      window.location.replace(`${normalizedPath}${search}${hash}`);
      return;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const me = await api.me();
        if (cancelled) return;
        setUser(me.user || null);
        setConfig({
          configured: Boolean(me.configured ?? true),
          bypass: Boolean(me.bypass),
          redirectUri: me.redirectUri || '',
          providers: me.providers,
        });
      } catch {
        if (cancelled) return;
        try {
          setConfig(await api.authConfig());
        } catch {
          setConfig(null);
        }
        setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleLogout() {
    try {
      await api.logout();
    } finally {
      for (const key of Object.keys(sessionStorage)) {
        if (key.startsWith('gia-geoclient-access:') || key.startsWith('gia-group-access:')) {
          sessionStorage.removeItem(key);
        }
      }
      setUser(null);
    }
  }

  if (loading) {
    return (
      <div className="min-h-full flex items-center justify-center bg-brand-soft">
        <div className="card px-6 py-5 text-sm text-slate-600">Checking sign-in status...</div>
      </div>
    );
  }

  if (user) return <>{children(user, handleLogout)}</>;

  return (
    <LoginScreen
      config={config}
      error={error || callbackError}
      onClearError={() => setError(null)}
    />
  );
}

function providerStatus(
  config: AuthConfig | null,
  id: 'entra' | 'okta',
): AuthProviderStatus {
  return config?.providers?.[id] ?? DEFAULT_PROVIDER_STATUS;
}

function LoginScreen({
  config,
  error,
  onClearError,
}: {
  config: AuthConfig | null;
  error: string | null;
  onClearError: () => void;
}) {
  const entra = providerStatus(config, 'entra');
  const okta = providerStatus(config, 'okta');
  const anyAvailable = entra.available || okta.available;

  return (
    <div className="min-h-full bg-brand-soft flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-md card overflow-hidden">
        <div className="h-2 bg-brand-gradient" />
        <div className="px-8 py-8">
          <div className="w-12 h-12 rounded-xl bg-brand-purple-50 text-brand-purple-700 flex items-center justify-center mb-5">
            <ShieldCheck size={24} />
          </div>
          <h1 className="text-2xl font-semibold text-slate-900">Sign in to Resource Calculator</h1>
          <p className="text-sm text-slate-500 mt-2 leading-relaxed">
            Use your company account to access client workspaces, models, maturity intake, and
            reporting.
          </p>

          {error && (
            <div className="mt-5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              <div className="font-medium">Sign-in failed</div>
              <div className="mt-1 break-words">{error}</div>
              <button className="underline mt-2" onClick={onClearError}>
                Dismiss
              </button>
            </div>
          )}

          {!anyAvailable && (
            <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              No sign-in providers are available. Configure Microsoft Entra or Okta environment
              variables and enable at least one login option.
              {config?.redirectUri && (
                <div className="mt-2 font-mono text-xs break-all">
                  Redirect URI: {config.redirectUri}
                </div>
              )}
            </div>
          )}

          <div className="mt-6 space-y-3">
            <ProviderLoginButton
              label="Sign in with Microsoft"
              href="/auth/login?provider=entra"
              available={entra.available}
              disabledReason={loginDisabledReason(entra, 'Microsoft Entra')}
            />
            <ProviderLoginButton
              label="Sign in with Okta"
              href="/auth/login?provider=okta"
              available={okta.available}
              disabledReason={loginDisabledReason(okta, 'Okta')}
              variant="secondary"
            />
          </div>

          <div className="text-xs text-slate-400 mt-5 leading-relaxed">
            This app uses a server-side session cookie. Identity-provider tokens are exchanged by
            the server and are not stored in the browser.
          </div>
        </div>
      </div>
    </div>
  );
}

function loginDisabledReason(status: AuthProviderStatus, label: string): string | undefined {
  if (status.available) return undefined;
  if (!status.enabled) return `${label} sign-in is disabled for this environment.`;
  if (!status.configured) return `${label} is not configured yet.`;
  return `${label} sign-in is unavailable.`;
}

function ProviderLoginButton({
  label,
  href,
  available,
  disabledReason,
  variant = 'primary',
}: {
  label: string;
  href: string;
  available: boolean;
  disabledReason?: string;
  variant?: 'primary' | 'secondary';
}) {
  const className = [
    variant === 'primary' ? 'btn-primary' : 'btn-secondary',
    'w-full',
    available ? '' : 'pointer-events-none opacity-60',
  ]
    .filter(Boolean)
    .join(' ');

  if (available) {
    return (
      <a href={href} className={className}>
        <LogIn size={16} />
        {label}
      </a>
    );
  }

  return (
    <button type="button" className={className} disabled title={disabledReason}>
      <LogIn size={16} />
      {label}
    </button>
  );
}
